import { memo, useEffect, useState } from "react";
import { CustomChart } from "../../../../../../../../../components/chart/CustomChart";
import { SellertoolSignatureUtils } from "../../../../../../../../../utils/sellertool-signature-utils";
import { AnalyticsApi } from "../../../../../../../../../apis/AnalyticsApi";
import moment from "moment";
import styled from 'styled-components';

const StyleContainer = styled.div`
    height: 500px;
    margin-top: 40px;
`;

export const FragmentOrderUnitGraph = memo(FragmentOrderUnitGraphView);

function FragmentOrderUnitGraphView({
    analyticsProductOptionsDetailList,
    releaseFrom,
    releaseTo,
    apiKey,
    secretKey,
}) {
    const optionCodesStr = analyticsProductOptionsDetailList?.map(r => r?.productOptionCode?.trim()).join(',');

    const [orderUnitList, setOrderUnitList] = useState(null);

    useEffect(() => {
        if (!apiKey || !secretKey || !optionCodesStr) {
            return;
        }

        handleReqFetchSearchOrderUnitForSales();
    }, [
        apiKey,
        secretKey,
        optionCodesStr
    ]);

    const handleReqFetchSearchOrderUnitForSales = async () => {
        const timestamp = Date.now().toString();
        const signature = SellertoolSignatureUtils().generateSigniture({
            apiKey: apiKey,
            secretKey: secretKey,
            timestamp: timestamp
        })

        const headers = {
            'x-sellertool-apiKey': apiKey,
            'x-sellertool-timestamp': timestamp,
            'x-sellertool-signiture': signature
        }

        const params = {
            channelOrderDateFrom: releaseFrom,
            channelOrderDateTo: releaseTo,
            optionCodes: optionCodesStr,
        }

        const response = await AnalyticsApi().searchOrderUnitListForSales({ headers, params });

        if (response?.content) {
            setOrderUnitList(response?.content);
        }
    }

    const dateRange = _getDateRange({
        fromDateQuery: releaseFrom,
        toDateQuery: releaseTo
    })

    const chartOptions = {
        ...CustomChart.defaultOptions,
        plugins: {
            ...CustomChart.defaultOptions.plugins,
            title: {
                display: true,
                text: '기간 별 주문량',
            },
        },
        scales: {
            ...CustomChart.defaultOptions.scales,
            x: {
                ...CustomChart.defaultOptions.scales.x,
                title: {
                    display: true,
                    text: '날짜',
                    font: {
                        size: 11,
                        weight: 600,
                    },
                },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 15,
                }
            },
            y: {
                ...CustomChart.defaultOptions.scales.y,
                title: {
                    display: true,
                    text: '주문량(개)',
                    font: {
                        size: 11,
                        weight: 600,
                    },
                },
                min: 0,
                // max: visitLogList ? visitLogList.reduce((max, visitLog) => (visitLog.totalVisitCountAmount > max.totalVisitCountAmount ? visitLog : max), visitLogList[0])?.totalVisitCountAmount + 5 : null,
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 7,
                    // y축 눈금을 정수로 설정
                    callback: function (value) {
                        if (Number.isInteger(value)) {
                            return value;
                        }
                    },
                },
                stacked: false
            }
        },
        onClick: (event, item) => {
            if (item?.length <= 0) {
                return;
            }
            let clickedIndex = item[0].index;

        },
        onHover: (event, chartElement) => {
            event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        },
    }

    const data = {
        labels: dateRange || [],
        datasets: orderUnitList ? orderUnitList?.map((r, index) => {
            const randomColor = HEX_COLOR_LIST[index];

            const productOptionDetail = analyticsProductOptionsDetailList?.find(r2 => r2?.productOptionCode === r?.productOptionCode);
            const label = (productOptionDetail?.productDto && productOptionDetail?.productOptionDto) ? `${productOptionDetail?.productDto?.name}&&${productOptionDetail?.productOptionDto?.name}` : r?.productOptionCode;
            return {
                type: 'line',
                label: label,
                data: r?.orderUnitList ? [...r?.orderUnitList?.map(r2 => r2?.quantity)] : [],
                borderColor: randomColor,
                backgroundColor: randomColor,
                borderWidth: 2,
                fill: false,
                barPercentage: 0.4,
                categoryPercentage: 1,
                order: 0,
                borderRadius: 5,
                tension: 0.3,
                stack: 'Stack 0',
            }
        }) : [],
    }

    return (
        <>
            <StyleContainer>
                {(dateRange && orderUnitList) &&
                    <CustomChart.Chart
                        options={chartOptions}
                        data={data}
                    />
                }
            </StyleContainer>
        </>
    );
}

// - fromDateQuery, toDateQuery 는 YYYY-MM-DD 형식으로 들어온다.
function _getDateRange({ fromDateQuery, toDateQuery }) {
    // - fromDateQuery 부터 toDateQuery 까지의 날짜 범위를 리스트로 가져온다.
    const fromDateMoment = moment(fromDateQuery);
    const toDateMoment = moment(toDateQuery);
    const dateList = [];
    for (let i = 0; i <= toDateMoment.diff(fromDateMoment, "days"); i++) {
        const date = moment(fromDateMoment).add(i, "days").format("YYYY-MM-DD");
        dateList.push(date);
    }
    return dateList;
}

const HEX_COLOR_LIST = [
    "#1f77b4", "#aec7e8", "#ff7f0e", "#ffbb78", "#2ca02c", "#98df8a", "#d62728", "#ff9896", "#9467bd", "#c5b0d5",
    "#8c564b", "#c49c94", "#e377c2", "#f7b6d2", "#7f7f7f", "#c7c7c7", "#bcbd22", "#dbdb8d", "#17becf", "#9edae5",
    "#8dd3c7", "#ffffb3", "#bebada", "#fb8072", "#80b1d3", "#fdb462", "#b3de69", "#fccde5", "#d9d9d9", "#bc80bd",
    "#db5f57", "#db6757", "#db6f57", "#db7757", "#db7e57", "#db8657", "#db8e57", "#db9657", "#db9e57", "#dba657",
    "#dbad57", "#dbb557", "#dbbd57", "#dbc557", "#dbcd57", "#dbd457", "#dbdc57", "#dbe457", "#dbec57", "#dbf457",
    "#d3f457", "#cbf457", "#c3f457", "#bbf457", "#b3f457", "#aaf457", "#a2f457", "#9af457", "#92f457", "#8af457",
    "#82f457", "#7af457", "#72f457", "#6af457", "#62f457", "#5af457", "#57f45f", "#57f467", "#57f46f", "#57f477",
    "#57f47e", "#57f486", "#57f48e", "#57f496", "#57f49e", "#57f4a6", "#57f4ad", "#57f4b5", "#57f4bd", "#57f4c5",
    "#57f4cd", "#57f4d4", "#57f4dc", "#57f4e4", "#57f4ec", "#57f4f4", "#57ecf4", "#57e4f4", "#57dcf4", "#57d4f4",
    "#57ccf4", "#57c4f4", "#57bcf4", "#57b4f4", "#57acf4", "#57a4f4", "#579cf4", "#5794f4", "#578cf4", "#5784f4",
    "#577cf4", "#5774f4", "#576cf4", "#5764f4", "#575cf4", "#5754f4", "#5c57f4", "#6457f4", "#6c57f4", "#7457f4",
    "#7c57f4", "#8457f4", "#8c57f4", "#9457f4", "#9c57f4", "#a457f4", "#ac57f4", "#b457f4", "#bc57f4", "#c457f4",
    "#cc57f4", "#d457f4", "#dc57f4", "#e457f4", "#ec57f4", "#f457f4", "#f457ec", "#f457e4", "#f457dc", "#f457d4"
];
